import { useEffect } from 'react';

// Performance monitoring hook
export const usePerformanceMonitoring = () => {
  useEffect(() => {
    // Only run in production
    if (process.env.NODE_ENV !== 'production') return;

    // Web Vitals monitoring
    const reportWebVitals = (metric) => {
      console.log('Web Vital:', metric);
      
      // You can send this data to analytics
      if (window.gtag) {
        window.gtag('event', metric.name, {
          custom_parameter_1: metric.value,
          custom_parameter_2: metric.id,
          custom_parameter_3: metric.name
        });
      }
    };

    // Core Web Vitals
    const observeWebVitals = () => {
      // Largest Contentful Paint (LCP)
      if ('PerformanceObserver' in window) {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          reportWebVitals({
            name: 'LCP',
            value: lastEntry.startTime,
            id: 'lcp'
          });
        });
        
        try {
          lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch (e) {
          console.warn('LCP observation not supported');
        }

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            reportWebVitals({
              name: 'FID',
              value: entry.processingStart - entry.startTime,
              id: 'fid'
            });
          });
        });

        try {
          fidObserver.observe({ entryTypes: ['first-input'] });
        } catch (e) {
          console.warn('FID observation not supported');
        }

        // Cumulative Layout Shift (CLS)
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          
          reportWebVitals({
            name: 'CLS',
            value: clsValue,
            id: 'cls'
          });
        });

        try {
          clsObserver.observe({ entryTypes: ['layout-shift'] });
        } catch (e) {
          console.warn('CLS observation not supported');
        }
      }
    };

    // Resource loading performance
    const observeResourceTiming = () => {
      if ('PerformanceObserver' in window) {
        const resourceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (entry.initiatorType === 'img' && entry.duration > 1000) {
              console.warn(`Slow image loading: ${entry.name} took ${entry.duration}ms`);
            }
          });
        });

        try {
          resourceObserver.observe({ entryTypes: ['resource'] });
        } catch (e) {
          console.warn('Resource timing observation not supported');
        }
      }
    };

    // Memory usage monitoring
    const monitorMemoryUsage = () => {
      if ('memory' in performance) {
        const memoryInfo = performance.memory;
        if (memoryInfo.usedJSHeapSize > memoryInfo.jsHeapSizeLimit * 0.9) {
          console.warn('High memory usage detected');
        }
      }
    };

    // Initialize monitoring
    observeWebVitals();
    observeResourceTiming();
    
    // Check memory every 30 seconds
    const memoryInterval = setInterval(monitorMemoryUsage, 30000);

    // Navigation timing
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation) {
          const metrics = {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
            ttfb: navigation.responseStart - navigation.requestStart,
            domInteractive: navigation.domInteractive - navigation.fetchStart
          };
          
          console.log('Navigation metrics:', metrics);
          
          // Report slow metrics
          if (metrics.ttfb > 600) console.warn('Slow TTFB:', metrics.ttfb);
          if (metrics.domContentLoaded > 1500) console.warn('Slow DOM Content Loaded:', metrics.domContentLoaded);
        }
      }, 0);
    });

    return () => {
      clearInterval(memoryInterval);
    };
  }, []);
};

// Performance metrics component
export const PerformanceMonitor = ({ children }) => {
  usePerformanceMonitoring();
  return children;
};
